import React, { useEffect, useState } from 'react';
import { Box, Grid, styled, Typography, Collapse, IconButton, FormControl, MenuItem, TextField, Select, InputAdornment, InputLabel, CircularProgress } from '@mui/material';
import { ToggleOffOutlined, ToggleOnOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { API } from '../utils/Api';
import { toast } from 'react-toastify';
import Images from '../utils/Images';
import axios from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import AddonRawMaterial from './AddonRawMaterials';

const ExpandIcon = styled(ExpandMoreOutlined)(({ expand }) => ({
    transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s ease-in-out',
}));

const AddonListing = () => {
    const token = localStorage.getItem('token');
    const [addonsList, setAddonsList] = useState([]);
    const [openMap, setOpenMap] = useState({});
    const [loading, setLoading] = useState(false);
    const storedOutletData = localStorage.getItem('outletData');
    const outletData = JSON.parse(storedOutletData);
    const outletId = outletData?._id;
    const [stockFilter, setStockFilter] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredAddons, setFilteredAddons] = useState([]);

    const toggleOpen = (id) => {
        setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const getAddonsList = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${API.BASE_URL}addons?page=1&limit=1000`, { headers });
            if (response?.data?.status) {
                const addons = response?.data?.data?.addons;
                setAddonsList(addons);
                setFilteredAddons(addons);
            }
            setLoading(false);
        } catch (error) {
            toast.error("Failed to fetch addons", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored"
            });
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAddonValueForOutlet = async (addon, value = null) => {
        const outletData = JSON.parse(localStorage.getItem('outletData'));
        const outletId = outletData?._id;
        const token = localStorage.getItem('token');

        const isAddonLevel = value === null;

        const isCurrentlyExcluded = isAddonLevel
            ? addon.excludedOutlets?.some((outlet) => outlet._id === outletId)
            : value.excludedOutlets?.some((outlet) => outlet._id === outletId);

        let payload = {
            addonData: {
                _id: addon._id,
                isOutletExcluded: isAddonLevel ? !isCurrentlyExcluded : false
            },
            addonValuesData: []
        };

        if (!isAddonLevel) {
            // Toggling a specific addon value
            payload.addonValuesData.push({
                _id: value._id,
                isOutletExcluded: !isCurrentlyExcluded
            });
        } else if (!payload.addonData.isOutletExcluded) {
            // Allocating the entire addon: required to pass all values
            payload.addonValuesData = addon.addonValues.map(val => ({
                _id: val._id,
                isOutletExcluded: false
            }));
        }

        try {
            const headers = {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            };

            const response = await axios.put(`${API.BASE_URL}addons/include-exclude-outlet-for-addons`, payload, { headers });

            if (response?.data?.status) {
                toast.success('Addon updated successfully', {
                    position: 'top-right',
                    autoClose: 3000,
                    theme: 'colored',
                });
                getAddonsList(); // refresh data
            } else {
                toast.error('Failed to update addon', {
                    position: 'top-right',
                    autoClose: 3000,
                    theme: 'colored',
                });
            }
        } catch (error) {
            let errorMessage = 'Something went wrong';
            if (error?.response) {
                const { status, data } = error.response;
                if ([400, 406, 412, 500].includes(status)) {
                    errorMessage = data?.error?.message || data?.error || data?.message;
                }
            }
            toast.error(errorMessage, {
                position: 'top-right',
                autoClose: 3000,
                theme: 'colored',
            });
        }
    };

    const handleSearch = (searchText = searchQuery, stockStatus = stockFilter) => {
        setSearchQuery(searchText);

        const filtered = addonsList
            .map(addon => {
                const matchesSearch = addon.title?.toLowerCase().includes(searchText.toLowerCase());

                // Filter addon values based on stock status
                let filteredValues = addon.addonValues || [];

                if (stockStatus === 'InStock') {
                    filteredValues = filteredValues.filter(
                        value => !value.excludedOutlets?.some(outlet => outlet._id === outletId)
                    );
                } else if (stockStatus === 'Exhausted') {
                    filteredValues = filteredValues.filter(
                        value => value.excludedOutlets?.some(outlet => outlet._id === outletId)
                    );
                }

                return {
                    ...addon,
                    addonValues: filteredValues,
                    _matchesSearch: matchesSearch,
                };
            })
            .filter(addon =>
                // Keep addons that match search and have visible values
                addon._matchesSearch && addon.addonValues.length > 0
            );

        setFilteredAddons(filtered);
    };

    useEffect(() => {
        getAddonsList();
    }, [])

    return (
        <>
            <Grid container md={11} sx={{ marginTop: '15px' }}>
                <Grid item md={3}>
                    <Typography sx={{ fontSize: '15px', margin: '16px 0 0 40px' }}>
                        Stock Inventory
                    </Typography>
                </Grid>
                <Grid item md={6}>
                    <TextField
                        fullWidth
                        sx={{
                            backgroundColor: '#fff',
                            marginTop: '5px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                            },
                            '& input': {
                                height: '10px',
                            },
                        }}
                        placeholder="Search"
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        value={searchQuery}
                        onChange={(e) => {
                            handleSearch(e.target.value);
                        }}
                    />
                </Grid>
                <Grid item md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControl fullWidth sx={{ marginLeft: '7px' }} size="small">
                        <InputLabel>Stock Status</InputLabel>
                        <Select
                            value={stockFilter}
                            label="Stock Status"
                            onChange={(e) => {
                                const newStatus = e.target.value;
                                setStockFilter(newStatus);
                                handleSearch(searchQuery, newStatus);
                            }}
                        >
                            <MenuItem value="All">All</MenuItem>
                            <MenuItem value="InStock">In Stock</MenuItem>
                            <MenuItem value="Exhausted">Out of Stock</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            <Grid item md={2} sx={{ padding: '20px 10px 20px 10px', borderRight: '1px solid grey' }}>
                <Typography sx={{ textAlign: 'center', fontSize: '15px' }}>
                    Item No
                </Typography>
            </Grid>
            <Grid item md={6} sx={{ padding: '20px 0px 20px 0px', borderRight: '1px solid grey' }}>
                <Typography sx={{ textAlign: 'center', fontSize: '15px' }}>
                    Addon
                </Typography>
            </Grid>
            <Grid item md={4} sx={{ padding: '20px 0px 20px 0px' }}>
                <Typography sx={{ textAlign: 'center', fontSize: '15px' }}>
                    Set as Out of Stock
                </Typography>
            </Grid>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
                    <CircularProgress />
                </Box>
            ) : (
                filteredAddons?.map((addon, index) => {
                    console.log('Outlet Id', addon?.excludedOutlets?.some((outlet) => outlet._id === outletId))
                    return (
                        <Grid
                            container
                            key={addon._id}
                            sx={{
                                backgroundColor: '#ffffff',
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid #e0e0e0',
                            }}
                        >
                            <Grid item container sx={{ backgroundColor: '#f5f5f5', alignItems: 'center' }}>
                                <Grid item md={2} sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography fontWeight={600}>{index + 1}</Typography>
                                </Grid>
                                <Grid item md={6} sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: 2, py: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {addon.badgeImage && (
                                            <img
                                                src={addon.badgeImage}
                                                alt={addon.title}
                                                style={{ height: 50, width: 50, borderRadius: 8, objectFit: 'cover' }}
                                            />
                                        )}
                                        <Typography fontWeight="bold" sx={{ fontSize: 16, paddingLeft: '18px' }}>{addon.title}</Typography>
                                    </Box>
                                    <IconButton onClick={() => toggleOpen(addon._id)} size="small">
                                        <ExpandIcon expand={openMap[addon._id]} />
                                    </IconButton>
                                </Grid>
                                <Grid item md={4} sx={{ textAlign: 'center', py: 2 }}>
                                    <IconButton onClick={() => handleToggleAddonValueForOutlet(addon)}>
                                        {addon.excludedOutlets?.some((outlet) => outlet._id === outletId) ? (
                                            <ToggleOffOutlined sx={{ fontSize: 28, color: 'red' }} />
                                        ) : (
                                            <ToggleOnOutlined sx={{ fontSize: 28, color: 'green' }} />
                                        )}
                                    </IconButton>
                                    <Typography sx={{ fontSize: 13 }}>
                                        {addon.excludedOutlets?.some((outlet) => outlet._id === outletId) ? 'Set Active' : 'Set Inactive'}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Collapse in={openMap[addon?._id]} timeout="auto" unmountOnExit>
                                    {addon?.addonValues?.map((value, valIndex) => {
                                        console.log('Outlet Id', addon?.excludedOutlets?.some((outlet) => outlet?._id === outletId));
                                        console.log('addon', addon);

                                        return (
                                            <Grid
                                                container
                                                key={value?._id}
                                                sx={{
                                                    px: 2,
                                                    py: 1,
                                                    borderTop: '1px solid #eee',
                                                    backgroundColor: '#fafafa',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Grid item md={2} sx={{ textAlign: 'center' }}>
                                                    <Typography sx={{ fontSize: 14, color: '#666' }}>
                                                        {index + 1}.{valIndex + 1}
                                                    </Typography>
                                                </Grid>

                                                <Grid item md={6} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Typography sx={{ fontSize: 15 }}>{value.value}</Typography>
                                                    {value.isDefault &&
                                                        <Typography>Default</Typography>
                                                    }
                                                </Grid>

                                                <Grid item md={4} sx={{ textAlign: 'center' }}>
                                                    <IconButton onClick={() => handleToggleAddonValueForOutlet(addon, value)}>
                                                        {value.excludedOutlets?.some((outlet) => outlet._id === outletId) ? (
                                                            <ToggleOffOutlined sx={{ fontSize: 28, color: 'red' }} />
                                                        ) : (
                                                            <ToggleOnOutlined sx={{ fontSize: 28, color: 'green' }} />
                                                        )}
                                                    </IconButton>
                                                    <Typography sx={{ fontSize: 13 }}>
                                                        {value.excludedOutlets?.some((outlet) => outlet._id === outletId) ? 'Set Active' : 'Set Inactive'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        )
                                    })}
                                </Collapse>
                            </Grid>
                        </Grid>
                    )
                })
            )}
            <AddonRawMaterial />
        </>
    )
}

export default AddonListing;